import axios from "axios";
import { UploadCloud } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { ManualInvoiceForm } from "../../containers/admin/ManualInvoiceForm";
import { useInvoices } from "../../helper/AccountingContext";

interface Kostenstelle {
  id: number;
  name: string;
}
interface PaymentReason {
  id: number;
  reason: string;
}
interface FinancingPlanItem {
  id: number;
  item: string;
}

export interface EditableInvoiceFormData {
  id?: number;
  invoiceNumber?: string;
  invoiceDate?: Date | string;
  sellerName?: string;
  sellerPostalCode?: string;
  sellerCity?: string;
  sellerStreet?: string;
  buyerName?: string;
  buyerPostalCode?: string;
  buyerCity?: string;
  buyerStreet?: string;
  grossAmount?: number;
  netAmount?: number;
  currencyCode?: string;
  dueDate?: Date | string;
  paymentReference?: string;
  paymentMethod?: string;
  iban?: string;
  bic?: string;
  vatAmount?: number;
  vatRate?: number;
  orderDate?: Date | string;
  paymentDate?: Date | string;
  paymentReason?: string;
  financingPlanItem?: string;
  kostenstelle?: string;
}

interface AccountingTableProps {
  onSuccess?: () => void;
}

export const AccountingTable: React.FC<AccountingTableProps> = ({ onSuccess }) => {
  const { createInvoice } = useInvoices();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewFile, setpreviewFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<Record<string, Partial<EditableInvoiceFormData>>>({});
  const [kostenstellen, setKostenstellen] = useState<Kostenstelle[]>([]);
  const [reason, setReason] = useState<PaymentReason[]>([]);
  const [item, setItem] = useState<FinancingPlanItem[]>([]);
  const [sellers, setSellers] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const pdfUrl = useObjectUrl(previewFile);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const fetchData = async () => {
      try {
        const [ksRes, reasonRes, itemRes, sellerRes] = await Promise.all([
          axios.get("/api/accounting/kostenstellen", { headers: { Authorization: `Bearer ${token}` } }),
          axios.get("/api/accounting/payment-reasons", { headers: { Authorization: `Bearer ${token}` } }),
          axios.get("/api/accounting/financing-plan-items", { headers: { Authorization: `Bearer ${token}` } }),
          axios.get("/api/accounting/sellers", { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setKostenstellen(ksRes.data);
        setReason(reasonRes.data);
        setItem(itemRes.data);
        setSellers(sellerRes.data);
      } catch (err) {
        console.error("Fehler beim Laden der Auswahlfelder:", err);
      }
    };

    fetchData();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setSelectedFiles((prev) => [...prev, ...fileArray]);
      setpreviewFile!(fileArray[0]);
    }
  };

  // Nimmt den String aus <input type="date" /> (immer "YYYY-MM-DD")
  // und speichert ihn bei dir als ISO-String "YYYY-MM-DD"
  function fromDateInputValue(value: string): string | undefined {
    if (!value) return undefined;
    return value; // wir speichern ab jetzt einfach den "YYYY-MM-DD"-String, kein Date-Objekt mehr
  }

  const removeFile = (index: number) => {
    const fileToRemove = selectedFiles[index];
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => {
      const newState = { ...prev };
      delete newState[fileToRemove.name];
      return newState;
    });
    if (previewFile! === fileToRemove) {
      setpreviewFile!(null);
    }
  };

  function useObjectUrl(file: File | null) {
    const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => {
      return () => {
        if (url) URL.revokeObjectURL(url);
      };
    }, [url]);
    return url;
  }

  const handleUpload = async () => {
    const token = localStorage.getItem("authToken");

    for (const file of selectedFiles) {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      try {
        const response = await axios.post("/api/accounting/upload-invoice", formDataUpload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        setFormData((prev) => ({
          ...prev,
          [file.name]: response.data.invoice,
        }));
      } catch (error) {
        console.error(`Fehler beim Upload von ${file.name}:`, error);
      }
    }

    if (selectedFiles.length > 0) {
      setpreviewFile!(selectedFiles[0]);
      setIsModalOpen(true);
    }
  };

  const handleCreateInvoice = async () => {
    if (!previewFile!) return;
    await saveSingleInvoice(previewFile!.name);
  };

  const saveSingleInvoice = async (filename: string) => {
    const token = localStorage.getItem("authToken");
    const invoiceData = formData[filename];

    if (!invoiceData?.kostenstelle || !invoiceData?.paymentReason || !invoiceData?.financingPlanItem) {
      alert(`Bitte alle Pflichtfelder ausfüllen (${filename})`);
      return;
    }

    const payload = {
      ...invoiceData,
      invoiceDate: invoiceData.invoiceDate ? new Date(invoiceData.invoiceDate) : undefined,
      dueDate: invoiceData.dueDate ? new Date(invoiceData.dueDate) : undefined,
      orderDate: invoiceData.orderDate ? new Date(invoiceData.orderDate) : undefined,
      paymentDate: invoiceData.paymentDate ? new Date(invoiceData.paymentDate) : undefined,
    };

    try {
      if (invoiceData.id) {
        // PATCH für bereits hochgeladene PDF-Rechnungen
        await axios.patch(`/api/accounting/invoice/${invoiceData.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        // POST für manuelle Rechnungen ohne ID
        await axios.post("/api/accounting/invoices", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setFormData((prev) => {
        const updated = { ...prev };
        delete updated[filename];
        return updated;
      });
      setSelectedFiles((prev) => prev.filter((f) => f.name !== filename));
      if (previewFile!?.name === filename) setpreviewFile!(null);
      onSuccess?.();
    } catch (error: any) {
      console.error(`Fehler beim Speichern (${filename}):`, error);
      alert(error.response?.data?.message || "Fehler beim Speichern.");
    }
  };

  const handleSaveAllInvoices = async () => {
    const allFilenames = Object.keys(formData);
    for (const filename of allFilenames) {
      await saveSingleInvoice(filename);
    }
    setIsModalOpen(false);
  };

  const currentFormData = previewFile! ? formData[previewFile!.name] || {} : {};
  const updateCurrentForm = (field: keyof EditableInvoiceFormData, value: any) => {
    if (!previewFile!) return;
    setFormData((prev) => ({
      ...prev,
      [previewFile!.name]: {
        ...prev[previewFile!.name],
        [field]: value,
      },
    }));
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-2 items-start">
        <h2 className="text-2xl font-bold">Rechnung hochladen</h2>
        <p className="text-muted-foreground">
          Wähle eine PDF-Datei aus, um sie in der Vorschau zu bearbeiten und anschließend zu speichern.
        </p>
      </div>

      <div className="flex gap-4 w-full">
        <div className="flex items-center justify-center w-full gap-8">
          <label
            htmlFor="dropzone-file"
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const files = Array.from(e.dataTransfer.files).filter((file) => file.type === "application/pdf");
              if (files.length > 0) {
                setSelectedFiles((prev) => [...prev, ...files]);
                setpreviewFile!(files[0]);
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            className={`flex flex-col items-center justify-center w-1/2 h-64 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
              isDragging
                ? "bg-primary/10 border-primary text-primary"
                : "bg-muted/50 hover:bg-muted text-muted-foreground"
            }`}>
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <UploadCloud className="w-8 h-8 mb-4" />
              <p className="mb-2 text-sm">
                <span className="font-semibold">Datei wählen</span> oder per Drag & Drop ablegen
              </p>
              <p className="text-xs">Nur PDF-Dateien (max. 5MB)</p>
            </div>
            <input id="dropzone-file" type="file" className="hidden" multiple onChange={handleFileChange} />
          </label>
          <ManualInvoiceForm
            kostenstellen={kostenstellen.map((k) => k.name)}
            paymentReasons={reason.map((r) => r.reason)}
            financingItems={item.map((i) => i.item)}
            sellers={sellers}
            onSuccess={onSuccess}
          />
        </div>
        {selectedFiles.length > 0 && (
          <div className="flex flex-row gap-4 w-full">
            <ul className="grid grid-cols-3 gap-2 h-fit">
              {selectedFiles.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-muted/30 px-4 py-2 rounded-md text-sm gap-8 h-[8ch]">
                  <span className="truncate w-full">{file.name}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="destructive" onClick={() => removeFile(index)}>
                      Entfernen
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogTrigger asChild>
          <Button
            onClick={async () => {
              setIsSaving(true);
              try {
                handleUpload();
              } catch (error) {
                return;
              } finally {
                setIsSaving(false);
              }
            }}
            disabled={selectedFiles.length === 0}
            className="w-fit self-end mt-2">
            Upload & Vorschau generieren
          </Button>
        </DialogTrigger>
        <DialogContent className="w-full max-w-[95vw] h-[95vh] overflow-auto">
          <div className="flex flex-col lg:flex-row gap-8 h-full">
            <div className="flex-1 overflow-y-auto">
              {selectedFiles.length > 1 && (
                <ul className="flex gap-2 mb-4 overflow-x-auto">
                  {selectedFiles.map((file, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={previewFile! === file ? "default" : "outline"}
                        onClick={() => setpreviewFile!(file)}>
                        {file.name.length > 20 ? file.name.slice(0, 17) + "..." : file.name}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              {previewFile! && (
                <object
                  data={pdfUrl ?? undefined}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  className="rounded-lg border">
                  <p>
                    PDF-Vorschau nicht verfügbar.{" "}
                    <a href={pdfUrl ?? undefined} target="_blank">
                      PDF öffnen
                    </a>
                    .
                  </p>
                </object>
              )}
            </div>
            <div className="w-full lg:w-[35%] flex flex-col gap-6 overflow-y-auto">
              <div>
                <Label>Rechnungsnummer</Label>
                <Input
                  value={currentFormData?.invoiceNumber || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!!.name]: {
                        ...prev[previewFile!!.name],
                        invoiceNumber: e.target.value,
                      },
                    }))
                  }
                />
              </div>
              <div>
                <Label>Verkäufer</Label>
                <Select
                  value={currentFormData.sellerName || ""}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!.name]: {
                        ...prev[previewFile!.name],
                        sellerName: value,
                      },
                    }))
                  }>
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sellers.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={currentFormData?.sellerName || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!!.name]: {
                        ...prev[previewFile!!.name],
                        sellerName: e.target.value,
                      },
                    }))
                  }
                />
                <Input
                  placeholder="Straße"
                  value={currentFormData?.sellerStreet || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!.name]: {
                        ...prev[previewFile!.name],
                        sellerStreet: e.target.value,
                      },
                    }))
                  }
                  className="mb-2"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="PLZ"
                    value={currentFormData.sellerPostalCode || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          sellerPostalCode: e.target.value,
                        },
                      }))
                    }
                  />
                  <Input
                    placeholder="Ort"
                    value={currentFormData.sellerCity || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          sellerCity: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              {/* Käufer */}
              <div>
                <Label>Käufer</Label>
                <Input
                  placeholder="Name"
                  value={currentFormData.buyerName || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!.name]: {
                        ...prev[previewFile!.name],
                        buyerName: e.target.value,
                      },
                    }))
                  }
                  className="mb-2"
                />
                <Input
                  placeholder="Straße"
                  value={currentFormData.buyerStreet || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!.name]: {
                        ...prev[previewFile!.name],
                        buyerStreet: e.target.value,
                      },
                    }))
                  }
                  className="mb-2"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="PLZ"
                    value={currentFormData.buyerPostalCode || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          buyerPostalCode: e.target.value,
                        },
                      }))
                    }
                  />
                  <Input
                    placeholder="Ort"
                    value={currentFormData.buyerCity || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          buyerCity: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              {/* Beträge */}
              <div>
                <Label>Beträge</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    placeholder="Bruttobetrag"
                    value={currentFormData.grossAmount || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          grossAmount: parseFloat(e.target.value),
                        },
                      }))
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Nettobetrag"
                    value={currentFormData.netAmount || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          netAmount: parseFloat(e.target.value),
                        },
                      }))
                    }
                  />
                  <Input
                    type="number"
                    placeholder="USt. Betrag"
                    value={currentFormData.vatAmount || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          vatAmount: parseFloat(e.target.value),
                        },
                      }))
                    }
                  />
                  <Input
                    type="number"
                    placeholder="USt. Satz (%)"
                    value={currentFormData.vatRate || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          vatRate: parseFloat(e.target.value),
                        },
                      }))
                    }
                  />
                  <Input
                    placeholder="Währung (z. B. EUR)"
                    value={currentFormData.currencyCode || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          currencyCode: e.target.value,
                        },
                      }))
                    }
                    className="col-span-2"
                  />
                </div>
              </div>

              {/* Bankdaten */}
              <div>
                <Label>Bankdaten</Label>
                <Input
                  placeholder="IBAN"
                  value={currentFormData.iban || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!.name]: {
                        ...prev[previewFile!.name],
                        iban: e.target.value,
                      },
                    }))
                  }
                  className="mb-2"
                />
                <Input
                  placeholder="BIC"
                  value={currentFormData.bic || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!.name]: {
                        ...prev[previewFile!.name],
                        bic: e.target.value,
                      },
                    }))
                  }
                  className="mb-2"
                />
                <Input
                  placeholder="Zahlungsmethode"
                  value={currentFormData.paymentMethod || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!.name]: {
                        ...prev[previewFile!.name],
                        paymentMethod: e.target.value,
                      },
                    }))
                  }
                  className="mb-2"
                />
                <Input
                  placeholder="Zahlungsreferenz"
                  value={currentFormData.paymentReference || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [previewFile!.name]: {
                        ...prev[previewFile!.name],
                        paymentReference: e.target.value,
                      },
                    }))
                  }
                />
              </div>

              <div>
                <Label>Datumsangaben</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Label>Rechnugsdatum</Label>
                  <Input
                    type="date"
                    value={
                      currentFormData.invoiceDate
                        ? new Date(currentFormData.invoiceDate).toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => updateCurrentForm("invoiceDate", new Date(e.target.value))}
                  />
                  <Label>Fälligkeitsdatum</Label>
                  <Input
                    type="date"
                    value={currentFormData.dueDate ? new Date(currentFormData.dueDate).toISOString().split("T")[0] : ""}
                    onChange={(e) => updateCurrentForm("dueDate", new Date(e.target.value))}
                  />
                  <Label>Bestelldatum</Label>
                  <Input
                    type="date"
                    value={
                      currentFormData.orderDate ? new Date(currentFormData.orderDate).toISOString().split("T")[0] : ""
                    }
                    onChange={(e) => updateCurrentForm("orderDate", new Date(e.target.value))}
                  />
                  <Label>Bezahldatum</Label>
                  <Input
                    type="date"
                    value={
                      currentFormData.paymentDate
                        ? new Date(currentFormData.paymentDate).toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => updateCurrentForm("paymentDate", new Date(e.target.value))}
                  />
                </div>
              </div>

              {/* Zuordnung (mit Select + Input für Freitext) */}
              <div className="flex flex-col gap-4">
                <div>
                  <Label>Kostenstelle</Label>
                  <Select
                    value={typeof currentFormData.kostenstelle === "string" ? currentFormData.kostenstelle : ""}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          kostenstelle: value,
                        },
                      }))
                    }>
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {kostenstellen.map((k) => (
                        <SelectItem key={k.id} value={k.name}>
                          {k.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Oder neue eingeben"
                    value={typeof currentFormData.kostenstelle === "string" ? currentFormData.kostenstelle : ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          kostenstelle: e.target.value,
                        },
                      }))
                    }
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Zahlungsgrund</Label>
                  <Select
                    value={currentFormData.paymentReason || ""}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          paymentReason: value,
                        },
                      }))
                    }>
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {reason.map((r) => (
                        <SelectItem key={r.id} value={r.reason}>
                          {r.reason}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Oder neuen eingeben"
                    value={currentFormData.paymentReason || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          paymentReason: e.target.value,
                        },
                      }))
                    }
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Finanzierungsposition</Label>
                  <Select
                    value={currentFormData.financingPlanItem || ""}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          financingPlanItem: value,
                        },
                      }))
                    }>
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {item.map((i) => (
                        <SelectItem key={i.id} value={i.item}>
                          {i.item}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Oder neue eingeben"
                    value={currentFormData.financingPlanItem || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [previewFile!.name]: {
                          ...prev[previewFile!.name],
                          financingPlanItem: e.target.value,
                        },
                      }))
                    }
                    className="mt-2"
                  />
                </div>
              </div>

              <Button onClick={handleSaveAllInvoices} className="w-full" variant="secondary">
                Alle angezeigten Rechnungen speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
